<script setup lang="ts">
import { ref, watch } from 'vue'

import {
  getScreenMediaOverwriteDialogState,
  resolveScreenMediaOverwriteFromDialog,
} from '@/composables/screenMediaOverwriteConfirm'

const { dialogOpen, dialogPayload } = getScreenMediaOverwriteDialogState()

const dialogRef = ref<HTMLDialogElement | null>(null)

watch(
  dialogOpen,
  (open) => {
    const el = dialogRef.value
    if (!el) return
    if (open) {
      if (!el.open) el.showModal()
    } else if (el.open) {
      el.close()
    }
  },
  { flush: 'post' },
)

function onConfirm(): void {
  resolveScreenMediaOverwriteFromDialog(true)
}

function onCancel(): void {
  resolveScreenMediaOverwriteFromDialog(false)
}
</script>

<template>
  <dialog
    ref="dialogRef"
    class="screenOverwriteDlg"
    aria-labelledby="screen-overwrite-title"
    @cancel.prevent="onCancel"
  >
    <div v-if="dialogPayload" class="dlgInner">
      <h2 id="screen-overwrite-title" class="dlgTitle">{{ dialogPayload.title }}</h2>
      <p class="dlgBody">{{ dialogPayload.body }}</p>
      <div class="dlgActions">
        <button type="button" class="btnGhost" @click="onCancel">{{ dialogPayload.cancelLabel }}</button>
        <button type="button" class="btnPrimary" @click="onConfirm">{{ dialogPayload.confirmLabel }}</button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.screenOverwriteDlg {
  max-width: 420px;
  width: calc(100% - 2rem);
  border: 1px solid var(--im-border-strong);
  border-radius: var(--im-radius-md);
  padding: 0;
  background: var(--im-surface);
  color: var(--im-text);
  box-shadow: var(--im-shadow);
}
.screenOverwriteDlg::backdrop {
  background: rgba(15, 23, 42, 0.45);
}
.dlgInner {
  padding: 1.1rem 1.25rem 1.15rem;
}
.dlgTitle {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 650;
}
.dlgBody {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--im-text-2);
}
.dlgActions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.btnGhost {
  border-radius: var(--im-radius-sm);
  border: 1px solid var(--im-border-strong);
  background: var(--im-surface-2);
  color: var(--im-text);
  padding: 0.45rem 0.85rem;
  font-size: 0.875rem;
  cursor: pointer;
}
.btnGhost:hover {
  background: var(--im-elevated);
}
.btnPrimary {
  border-radius: var(--im-radius-sm);
  border: 1px solid var(--im-accent);
  background: var(--im-interactive-bg);
  color: var(--im-interactive-fg);
  padding: 0.45rem 0.85rem;
  font-size: 0.875rem;
  cursor: pointer;
}
.btnPrimary:hover {
  background: var(--im-interactive-hover);
}
</style>
